// Puerto verbatim de packages/web/src/api/lib/catalogo-especialidades.ts.
// Cualquier cambio ahí debe replicarse acá a mano (mismo criterio que
// tramoB.ts).

export const PAQUETES = {
  /**
   * Ambulatorio base: casi todas las especialidades parten de esto.
   * 013A/013B (Anatomía Patológica), 054/055 (Concentrados de Laboratorio) y
   * 118 (Solicitud de Sangre) se suman acá 2026-09-05 — bug real reportado en
   * vivo: quedaban restringidos a 3-4 especialidades puntuales (gastro,
   * hematología, patología, cirugía cardíaca) pese a que CUALQUIER médico
   * tratante puede solicitar una biopsia, un hemoderivado o un concentrado de
   * laboratorio para su paciente, igual que ya pasa con 010A/010B/012A/012B
   * (Laboratorio/Imagenología) — la pestaña "Laboratorio y Estudios" quedaba
   * mostrando solo 4 de los 10 formularios de su grupo para la mayoría de
   * especialidades. Las especialidades `sinBase` (radiología, laboratorio
   * clínico, patología, neurofisiología, etc.) NO reciben este paquete —
   * siguen listando estos códigos aparte, en su propio `extras`.
   */
  // Los 6 consentimientos nuevos (ver hce-chart/src/lib/consentimientos.ts)
  // se suman acá 2026-09-09, mismo criterio que 024: consentimiento firmado,
  // no depende de qué ejerce el médico.
  base: [
    "001", "002", "005", "007", "010A", "010B", "012A", "012B", "013A", "013B", "054", "055", "118", "022", "024", "053",
    "consent-quirurgico", "consent-anestesia", "consent-transfusion", "consent-telesalud-canal", "consent-telesalud-lopdp", "consent-voluntad-anticipada",
  ],
  hospitalizacion: ["003", "006", "020"],
  quirurgico: ["017", "018", "018A", "019", "020", "006", "060"],
  // "052" (Planificación Familiar) se suma 2026-09-18 — Fase 5, ver PLAN.md.
  obstetrico: ["051", "052"],
  // "016A" (Test de Capurro/Ballard) y "016B"/"016C" (Fenton) se suman 2026-09-17/18 — Fase 5, ver PLAN.md.
  neonatal: ["016", "016A", "016B", "016C"],
  // "113" corregido 2026-09-16 (antes "116-uci-adulto" — ver PLAN.md).
  uciAdulto: ["113", "114", "115"],
  emergencia: ["008"],
  odontologico: ["033"],
  // Corrección de numeración 2026-09-15 (Serie 028, Tanda A — ver PLAN.md): antes "028A1-4".
  curvasCrecimiento: ["028A-1", "028B-1", "028B-2", "028C-1", "028C-2"],
  // "029"/"028D" retirados (Serie 028, Fase 2, Tandas 8-10): códigos
  // inventados, no existen en la fuente primaria oficial (ver
  // tasks/investigacion-028-aiepi-denver.md) — 028C (5-9a, sin AIEPI) es el
  // código real que reemplaza lo que "028D" intentaba cubrir.
  consultaPediatrica: ["028A", "028B", "028C"],
  /** Test de Denver II — tamizaje del desarrollo psicomotor (Serie 028, Fase 2, Tanda 11). */
  denverII: ["028"],
  pesoGestante: ["034"],
  tanner: ["056", "056A", "056B"],
  geriatrico: ["057", "057A"],
} as const;

/**
 * Códigos habilitados para CUALQUIER especialidad, sin excepción — no van
 * en `PAQUETES` porque no dependen de qué ejerce el médico. `codigosFormularioDeEspecialidad`
 * los suma primero, y una especialidad puntual puede sacarlos de nuevo con
 * `excluir` si de verdad no aplican (ver "enfermeria"/"paramedicos" para 117
 * — a pedido explícito del usuario 2026-09-01).
 *
 * `059` ya era "universal" a nivel de PESTAÑA (GRUPOS_UNIVERSALES en
 * packages/hce-chart/src/paciente/gruposHabilitados.ts) pero no estaba acá
 * — quedaba bloqueado igual al acceder por URL directa/guardar (el gate de
 * `FormularioDiscretoPage`/`MotorFormularioPage` usa esta función, no
 * `GRUPOS_UNIVERSALES`). Mismo bug encontrado de paso al agregar `117`.
 */
// `020` (Constantes Vitales) se suma 2026-09-11 — ver el comentario largo en
// packages/web/src/api/lib/catalogo-especialidades.ts (fuente canónica):
// la pestaña "Signos Vitales" ya era visible para todas las especialidades,
// pero el único formulario que la alimenta (020) vivía solo en
// hospitalizacion/quirurgico, así que quedaba vacía para el resto.
// "116-cert" se suma 2026-09-16 — ver catalogo-especialidades.ts (packages/web), fuente canónica.
export const CODIGOS_UNIVERSALES = ["059", "117", "020", "116-cert"];

export type CodigoPaquete = keyof typeof PAQUETES;

export type EspecialidadDef = {
  codigo: string;
  nombre: string;
  sinBase?: boolean;
  paquetes?: CodigoPaquete[];
  extras?: string[];
  excluir?: string[];
  pendientes?: string[];
};

export const CATALOGO_ESPECIALIDADES: EspecialidadDef[] = [
  // "geriatrico" también en Medicina General y Medicina Familiar y
  // Comunitaria — ver catalogo-especialidades.ts (packages/web), fuente
  // canónica.
  // "esc-phq9" (tamizaje de depresión) se suma acá, en Medicina Familiar y
  // Comunitaria, Psiquiatría y Psicología Clínica — escala de uso habitual
  // en atención primaria, no exclusiva de salud mental (Grupo 5, Módulo H).
  { codigo: "medicina-general", nombre: "Medicina General", paquetes: ["geriatrico"], extras: ["esc-phq9", "Anexo1"] },
  { codigo: "medicina-familiar-comunitaria", nombre: "Medicina Familiar y Comunitaria", paquetes: ["geriatrico"], extras: ["038", "esc-phq9", "Anexo1"] },
  { codigo: "medicina-interna", nombre: "Medicina Interna", paquetes: ["hospitalizacion"], extras: ["esc-nyha"] },
  { codigo: "alergologia-inmunologia-clinica", nombre: "Alergología e Inmunología Clínica" },
  // "esc-nyha" (clasificación funcional de insuficiencia cardíaca) se suma
  // acá y en Medicina Interna (Grupo 5, Módulo H).
  { codigo: "cardiologia", nombre: "Cardiología", paquetes: ["hospitalizacion"], extras: ["esc-nyha"] },
  { codigo: "endocrinologia-diabetologia", nombre: "Endocrinología / Diabetología" },
  { codigo: "gastroenterologia", nombre: "Gastroenterología" },
  { codigo: "geriatria-gerontologia", nombre: "Geriatría / Gerontología", paquetes: ["hospitalizacion", "geriatrico"], extras: ["116-cert", "058", "058A"] },
  // "122" (Registro Transfusional y Seguimiento) se suma acá y en cirugía
  // general/cardiaca, anestesiología y gineco-obstetricia — a pedido
  // explícito del usuario 2026-09-01 ("médicos transfusionistas,
  // hematólogos, cirujanos, anestesiólogos, ginecólogos y enfermeros"). Ver
  // también la especialidad nueva "medicina-transfusional" más abajo.
  { codigo: "hematologia", nombre: "Hematología", extras: ["122"] },
  { codigo: "infectologia", nombre: "Infectología", paquetes: ["hospitalizacion"], pendientes: ["EPI-1 (notificación obligatoria SINAVE)"] },
  { codigo: "nefrologia", nombre: "Nefrología", paquetes: ["hospitalizacion"], extras: ["038"] },
  { codigo: "neumologia", nombre: "Neumología" },
  { codigo: "reumatologia", nombre: "Reumatología" },
  { codigo: "toxicologia-medica", nombre: "Toxicología Médica", extras: ["008"] },
  // "058" (Cuidados Paliativos) se suma 2026-09-18 — Fase 5, ver PLAN.md.
  { codigo: "medicina-paliativa-cuidados-paliativos", nombre: "Medicina Paliativa / Cuidados Paliativos", extras: ["058", "058A"] },
  { codigo: "genetica-medica", nombre: "Genética Médica" },
  { codigo: "medicina-del-deporte", nombre: "Medicina del Deporte" },
  {
    codigo: "medicina-laboral-ocupacional",
    nombre: "Medicina Laboral / Ocupacional",
    sinBase: true,
    extras: ["FEMO"],
  },
  { codigo: "nutricion-clinica", nombre: "Nutrición Clínica" },
  { codigo: "cirugia-general", nombre: "Cirugía General", paquetes: ["quirurgico"], extras: ["122"] },
  { codigo: "cirugia-pediatrica", nombre: "Cirugía Pediátrica", paquetes: ["quirurgico", "neonatal"] },
  { codigo: "cirugia-plastica-reconstructiva", nombre: "Cirugía Plástica y Reconstructiva", paquetes: ["quirurgico"] },
  { codigo: "cirugia-toracica", nombre: "Cirugía Torácica", paquetes: ["quirurgico"] },
  { codigo: "cirugia-cardiaca-cardiovascular", nombre: "Cirugía Cardiaca / Cardiovascular", paquetes: ["quirurgico"], extras: ["122"] },
  { codigo: "cirugia-vascular-endovascular", nombre: "Cirugía Vascular y Endovascular", paquetes: ["quirurgico"] },
  { codigo: "cirugia-maxilofacial", nombre: "Cirugía Maxilofacial", paquetes: ["quirurgico"], extras: ["033"] },
  { codigo: "cirugia-cabeza-cuello", nombre: "Cirugía de Cabeza y Cuello", paquetes: ["quirurgico"] },
  { codigo: "neurocirugia", nombre: "Neurocirugía", paquetes: ["quirurgico"], extras: ["esc-glasgow"] },
  { codigo: "coloproctologia-proctologia", nombre: "Coloproctología / Proctología", paquetes: ["quirurgico"] },
  { codigo: "urologia", nombre: "Urología", paquetes: ["quirurgico"] },
  { codigo: "otorrinolaringologia", nombre: "Otorrinolaringología", paquetes: ["quirurgico"] },
  { codigo: "oftalmologia", nombre: "Oftalmología", paquetes: ["quirurgico"] },
  { codigo: "traumatologia-ortopedia", nombre: "Traumatología y Ortopedia", paquetes: ["quirurgico"] },
  // "017" se suma 2026-09-16 — ver catalogo-especialidades.ts (packages/web), fuente canónica.
  {
    codigo: "anestesiologia",
    nombre: "Anestesiología",
    sinBase: true,
    extras: ["017", "018", "018A", "019", "020", "024", "122"],
  },
  { codigo: "ginecologia-obstetricia", nombre: "Ginecología y Obstetricia", paquetes: ["quirurgico", "obstetrico", "pesoGestante"], extras: ["122"] },
  // "114" se suma 2026-09-16 — ver catalogo-especialidades.ts (packages/web), fuente canónica.
  { codigo: "neonatologia", nombre: "Neonatología", paquetes: ["hospitalizacion", "neonatal", "curvasCrecimiento"], extras: ["051", "114"] },
  {
    codigo: "pediatria",
    nombre: "Pediatría",
    // "tanner" también en Pediatría (no exclusivo de Hebeatría) — ver
    // catalogo-especialidades.ts (packages/web), fuente canónica.
    paquetes: ["hospitalizacion", "neonatal", "consultaPediatrica", "curvasCrecimiento", "tanner", "denverII"],
  },
  { codigo: "hebeatria-adolescentes", nombre: "Hebeatría (Adolescentes)", paquetes: ["tanner"] },
  // "115" se suma 2026-09-16 — ver catalogo-especialidades.ts (packages/web), fuente canónica.
  {
    codigo: "cuidados-intensivos-pediatricos",
    nombre: "Cuidados Intensivos Pediátricos",
    paquetes: ["hospitalizacion"],
    extras: ["115"],
    pendientes: ["Bitácora UCI Pediátrica (115) construida pero con motor genérico — falta ~95% de los campos oficiales, ver PLAN.md"],
  },
  { codigo: "psiquiatria", nombre: "Psiquiatría", extras: ["esc-phq9"] },
  { codigo: "psicologia-clinica", nombre: "Psicología Clínica", excluir: ["022"], extras: ["121", "esc-phq9"] },
  // "119" (Transferencia — nombre oficial corregido 2026-09-16, antes decía
  // "Transporte Secundario") se suma acá,
  // en Medicina Crítica/Terapia Intensiva, y en las especialidades nuevas
  // "paramedicos"/"enfermeria" más abajo — a pedido explícito del usuario
  // 2026-09-01. "esc-glasgow" (escala de coma) se suma acá, en Medicina
  // Crítica, Neurología y Neurocirugía (Grupo 5, Módulo H).
  // 024 sale de acá (2026-09-23, T-U8): el instructivo oficial (Fe de Errata AC-00115-2021, hoja 110 del visor) dice "Este formulario no
  // será llenado en el servicio de emergencia" y lista emergencia/urgencia entre las situaciones sin consentimiento informado.
  { codigo: "medicina-de-emergencia", nombre: "Medicina de Emergencia", sinBase: true, paquetes: ["emergencia"], extras: ["001", "020", "053", "119", "esc-glasgow", "Anexo2"] },
  { codigo: "medicina-critica-terapia-intensiva", nombre: "Medicina Crítica / Terapia Intensiva", paquetes: ["hospitalizacion", "uciAdulto"], extras: ["119", "esc-glasgow"] },
  { codigo: "radiologia-imagenologia", nombre: "Radiología e Imagenología", sinBase: true, extras: ["012A", "012B", "001"] },
  { codigo: "medicina-nuclear", nombre: "Medicina Nuclear", sinBase: true, extras: ["012A", "012B", "024"] },
  { codigo: "patologia-anatomia-patologica", nombre: "Patología / Anatomía Patológica", sinBase: true, extras: ["013A", "013B"] },
  {
    codigo: "laboratorio-clinico",
    nombre: "Laboratorio Clínico",
    sinBase: true,
    extras: ["010A", "010B", "054", "055"],
  },
  { codigo: "oncologia", nombre: "Oncología", paquetes: ["hospitalizacion"] },
  { codigo: "neurologia", nombre: "Neurología", paquetes: ["hospitalizacion"], extras: ["esc-glasgow"] },
  { codigo: "neurofisiologia-clinica", nombre: "Neurofisiología Clínica", sinBase: true, extras: ["010A", "010B", "012A", "012B", "013A", "013B", "001", "024"] },
  { codigo: "dermatologia", nombre: "Dermatología" },
  // "053" se suma 2026-09-16 — ver catalogo-especialidades.ts (packages/web), fuente canónica.
  {
    codigo: "odontologia-general-especialidades",
    nombre: "Odontología General y Especialidades Odontológicas",
    sinBase: true,
    paquetes: ["odontologico"],
    extras: ["024", "001", "053"],
  },
  { codigo: "fisiatria-medicina-fisica-rehabilitacion", nombre: "Fisiatría / Medicina Física y Rehabilitación" },
  {
    codigo: "medicina-legal-forense",
    nombre: "Medicina Legal / Forense",
    sinBase: true,
    pendientes: ["Fuera del flujo HCU-MSP — se rige por el informe pericial del SNMLCF, sin formularios propios del sistema"],
  },

  // Las 3 filas de abajo NO vienen de la matriz de 54 especialidades del
  // Excel MSP/ACESS (esa lista es de especialidades MÉDICAS puntuales) —
  // son roles nuevos agregados a pedido explícito del usuario 2026-09-01,
  // reusando el mismo selector de "especialidad" del registro de Personal
  // Médico porque el sistema no tiene hoy un selector de rol aparte.
  // "medicina-transfusional" sí es una especialidad médica real (quedó
  // fuera del Excel original); "enfermeria"/"paramedicos" son roles no
  // médicos — vale la pena que el usuario confirme si prefiere separarlos
  // en su propio selector más adelante, acá se resuelve con lo que ya existe.
  {
    codigo: "medicina-transfusional",
    nombre: "Medicina Transfusional",
    extras: ["122"],
  },
  {
    codigo: "enfermeria",
    nombre: "Enfermería",
    sinBase: true,
    // 117 (Certificado Médico) es universal (CODIGOS_UNIVERSALES) EXCEPTO
    // para enfermería y paramédicos — a pedido explícito del usuario
    // 2026-09-01, ver `excluir` abajo.
    // Fase 3 ("MSP + nota SOAPIE", decisión de Sue): Kardex = 022, balance
    // hídrico = 020 ampliado (secciones C-F), intervenciones = 120 — los tres
    // del Anexo 1 (docs/msp/). "enf-soapie" es la nota de turno narrativa:
    // código PROPIO de LogiaCare, NO un formulario MSP. Ver catalogo-especialidades.ts
    // (packages/web, fuente canónica).
    // 005 (Evolución y Prescripciones) se QUITA 2026-09-13, decisión de Sue:
    // una enfermera no prescribe ni escribe notas de evolución médicas (el 005
    // escribe ClinicalImpression, que el AccessPolicy "Enfermera" ya tenía en
    // `readonly` — el link directo solo llevaba a un 403).
    // "053" se suma 2026-09-16 — ver catalogo-especialidades.ts (packages/web), fuente canónica.
    extras: ["008", "020", "022", "119", "120", "122", "053", "enf-soapie", "Anexo2"],
    excluir: ["117"],
  },
  {
    codigo: "paramedicos",
    nombre: "Paramédicos",
    sinBase: true,
    extras: ["008", "020", "022", "119", "053", "Anexo2"],
    excluir: ["117"],
    pendientes: [
      "Formularios de inspección de ambulancias (ACESS, por nivel de soporte vital) — son papeleo de certificación del VEHÍCULO/establecimiento móvil, no un formulario de la Historia Clínica Única del paciente; no corresponden a esta tabla (especialidad→formulario clínico).",
    ],
  },
];

/**
 * Mismo catálogo, ordenado alfabéticamente por nombre — pedido explícito del
 * usuario 2026-09-11 para los desplegables de especialidad (registro de
 * Personal Médico en LandingAcceso.tsx, selector de demo). El array base
 * (`CATALOGO_ESPECIALIDADES`) queda SIN tocar y en su orden original: agrupa
 * especialidades emparentadas con comentarios explicativos entre lotes (ver
 * arriba), que perdería sentido si el array canónico se reordenara.
 * `localeCompare` con locale `es` para que "ñ"/acentos ordenen como en
 * español (ej. "Nutrición" antes que "Odontología").
 */
export const CATALOGO_ESPECIALIDADES_ORDENADO = [...CATALOGO_ESPECIALIDADES].sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'));

const POR_CODIGO = new Map(CATALOGO_ESPECIALIDADES.map((e) => [e.codigo, e]));

export function especialidadPorCodigo(codigo: string): EspecialidadDef | undefined {
  return POR_CODIGO.get(codigo);
}

/**
 * Especialidades con una política de acceso PROPIA y angosta en el servidor (`POLICY_POR_ESPECIALIDAD`, medplum-admin.ts). Todas las demás reciben la
 * política «Médico», que lee y escribe casi todo el expediente: pasar a una de ellas es una ampliación de acceso y el panel pide confirmarla.
 */
const ESPECIALIDADES_CON_POLITICA_ANGOSTA = ['enfermeria', 'paramedicos', 'psicologia-clinica', 'odontologia-general-especialidades'];

export function especialidadUsaPoliticaMedico(codigo: string): boolean {
  return POR_CODIGO.has(codigo) && !ESPECIALIDADES_CON_POLITICA_ANGOSTA.includes(codigo);
}

export function codigosFormularioDeEspecialidad(codigoEspecialidad: string): string[] {
  const def = POR_CODIGO.get(codigoEspecialidad);
  if (!def) return [];
  const set = new Set<string>(CODIGOS_UNIVERSALES);
  if (!def.sinBase) {
    for (const c of PAQUETES.base) set.add(c);
  }
  for (const p of def.paquetes ?? []) {
    for (const c of PAQUETES[p]) set.add(c);
  }
  for (const c of def.extras ?? []) set.add(c);
  for (const c of def.excluir ?? []) set.delete(c);
  return [...set];
}

/**
 * Igual que `codigosFormularioDeEspecialidad`, pero SIN sumar `extras` —
 * solo lo que viene de paquetes/base/universales. Uso exclusivo:
 * `calcularGruposHabilitados` (gruposHabilitados.ts), para decidir qué
 * PESTAÑA de grupo mostrar en el sidebar de la ficha. Bug real encontrado
 * en vivo 2026-09-02: Medicina General tiene `esc-phq9` como extra suelto
 * (una escala, no el paquete de Psicología Clínica) — como
 * `codigosFormularioDeEspecialidad` lo incluye, `calcularGruposHabilitados`
 * (que solo chequea "¿tiene AL MENOS un código de este grupo?") mostraba la
 * pestaña "Psicología Clínica" COMPLETA para Medicina General. La función de
 * arriba sigue siendo la que gatea abrir/guardar un formulario puntual
 * (`MotorFormularioPage.tsx`/`FormularioDiscretoPage.tsx`) — esc-phq9 sigue
 * siendo accesible para Medicina General, solo que no dispara una pestaña de
 * grupo ajena. Ver también `extrasSueltos` en gruposHabilitados.ts.
 */
export function codigosDePaquetesDeEspecialidad(codigoEspecialidad: string): string[] {
  const def = POR_CODIGO.get(codigoEspecialidad);
  if (!def) return [];
  const set = new Set<string>(CODIGOS_UNIVERSALES);
  if (!def.sinBase) {
    for (const c of PAQUETES.base) set.add(c);
  }
  for (const p of def.paquetes ?? []) {
    for (const c of PAQUETES[p]) set.add(c);
  }
  for (const c of def.excluir ?? []) set.delete(c);
  return [...set];
}
